import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, X } from 'lucide-react';
import mixpanel from 'mixpanel-browser';

import { cn } from '@/lib/utils';

/**
 * Drives the four NEA-5713 connection-health banners (GBP revoked,
 * POS disconnected, all-stock-rejected, stale ingest). Visually mirrors
 * `LifecycleBanner` so the dashboard chrome has one banner rhythm — same
 * max-width, same padding, same dark pill CTA for "urgent" — but driven by
 * caller-supplied content instead of `data/banners.json`.
 *
 * Severity → existing design tokens (matches the soft/hard idiom already in
 * `LifecycleBanner` + the destructive tint used on `ads/index.jsx`):
 *   - `soft`     info-style nudge, dismissible
 *   - `warning`  warning-soft, mid escalation
 *   - `urgent`   action-soft + dark CTA, the canonical "fix this now" look
 *   - `error`    destructive tint with destructive title, highest urgency
 *
 * The visual final word goes to Max once designs land — swap the
 * `SEVERITY_CONTAINER` / `SEVERITY_TITLE` maps then.
 */
const SEVERITY_CONTAINER = {
	soft: 'bg-primary/5 border-primary/10 text-foreground',
	warning: 'bg-warning-soft text-warning-soft-foreground border-warning/30',
	urgent: 'bg-action-soft text-action-soft-foreground border-action/20',
	error: 'bg-destructive/5 border-destructive/30 text-foreground'
};

const SEVERITY_TITLE = {
	soft: 'text-foreground',
	warning: 'text-warning-soft-foreground',
	urgent: 'text-action-soft-foreground',
	error: 'text-destructive'
};

const SEVERITY_ICON = {
	soft: 'text-primary',
	warning: 'text-warning-soft-foreground',
	urgent: 'text-action-soft-foreground',
	error: 'text-destructive'
};

const trackedImpressions = new Set();

// Test-only: tests that assert the "fires exactly once per (id, shopId)"
// behaviour need to clear the module-level dedup set between cases so a
// previously-tracked key doesn't silently make the assertion vacuous. Not
// exported for production callers.
export function __resetHealthBannerImpressionsForTests() {
	trackedImpressions.clear();
}

export function HealthBanner({
	id,
	severity = 'urgent',
	icon: Icon = AlertTriangle,
	leadingPill = null,
	title,
	body = null,
	ctaLabel = null,
	ctaTo = null,
	onDismiss = null,
	shopId = null
}) {
	useEffect(() => {
		if (!id) return;
		const key = `${id}:${shopId ?? 'no-shop'}`;
		if (trackedImpressions.has(key)) return;
		trackedImpressions.add(key);
		try {
			mixpanel.track('unify health banner impression', { id, severity, shopId });
		} catch {
			// Mixpanel may be uninitialised in dev / preview.
		}
	}, [id, severity, shopId]);

	const containerClass = cn('w-full border-b', SEVERITY_CONTAINER[severity] ?? SEVERITY_CONTAINER.urgent);
	const titleClass = cn('text-sm font-semibold', SEVERITY_TITLE[severity] ?? SEVERITY_TITLE.urgent);
	const iconClass = cn('h-4 w-4 shrink-0', SEVERITY_ICON[severity] ?? SEVERITY_ICON.urgent);

	const ctaButton =
		ctaLabel && ctaTo ? (
			severity === 'urgent' || severity === 'error' ? (
				<Link
					to={ctaTo}
					className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-foreground px-4 py-2 text-sm font-semibold text-background transition hover:opacity-90"
				>
					{ctaLabel} <ArrowRight className="h-3.5 w-3.5" />
				</Link>
			) : (
				<Link
					to={ctaTo}
					className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-semibold text-primary hover:underline"
				>
					{ctaLabel} <ArrowRight className="h-3.5 w-3.5" />
				</Link>
			)
		) : null;

	return (
		<div className={containerClass} data-banner-id={id}>
			<div className="mx-auto flex max-w-[1100px] items-start gap-3 px-6 py-2.5 md:px-10">
				{leadingPill ? (
					<span className="mt-0.5 inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-action px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-action-foreground">
						{leadingPill}
					</span>
				) : Icon ? (
					<Icon className={cn(iconClass, 'mt-0.5')} />
				) : null}

				<div className="min-w-0 flex-1">
					<div className={titleClass}>{title}</div>
					{body ? <p className="mt-0.5 text-xs text-muted-foreground">{body}</p> : null}
				</div>

				<div className="flex shrink-0 items-center gap-2">
					{ctaButton}
					{onDismiss ? (
						<button
							type="button"
							onClick={onDismiss}
							aria-label="Dismiss banner"
							className="rounded-md p-1 text-muted-foreground transition hover:text-foreground"
						>
							<X className="h-4 w-4" />
						</button>
					) : null}
				</div>
			</div>
		</div>
	);
}
